/**
 * useRentalStatus - Custom hook for managing rental status with interval updates
 * Calculates if a rental is approaching or past its max duration
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import axios from 'axios';
import { calculateRentalStatus, RENTAL_STATUS } from '../utils/rentalStatusUtils';

export const useRentalStatus = (reservation) => {
  const { token } = useAuth();
  const [rentalStatus, setRentalStatus] = useState(RENTAL_STATUS.NORMAL);
  const [settings, setSettings] = useState({
    maxRentalDurationHours: 0,
    notificationFlagMinutes: 0
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch organization settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/settings/organization/general', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data) {
          setSettings({
            maxRentalDurationHours: response.data.maxRentalDurationHours || 0,
            notificationFlagMinutes: response.data.notificationFlagMinutes || 0
          });
          setSettingsLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching rental notification settings:', error);
        setSettingsLoaded(true); // Mark as loaded even on error to prevent infinite loading
      }
    };

    fetchSettings();
  }, [token]);

  // Calculate status function
  const updateStatus = useCallback(() => {
    if (!reservation || !settingsLoaded) return;

    const status = calculateRentalStatus(
      reservation,
      settings.maxRentalDurationHours,
      settings.notificationFlagMinutes
    );
    setRentalStatus(status);
  }, [reservation, settings, settingsLoaded]);

  // Update status on mount and when settings change
  useEffect(() => {
    updateStatus();
  }, [updateStatus]);

  // Set up interval to update status every minute (60000ms)
  useEffect(() => {
    if (!settingsLoaded) return;

    const intervalId = setInterval(() => {
      updateStatus();
    }, 60000); // Update every minute

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [updateStatus, settingsLoaded]);

  return {
    rentalStatus,
    settings,
    settingsLoaded
  };
};

export default useRentalStatus;
